import { useState } from "react"
import classNames from "classnames"
import image, { PRODUCT_PHOTOGRAMME } from "lacinetek-common/src/images"

import { useTranslation } from "lib/i18n/translation"
import { useAuth } from "lib/auth"
import { useModal } from "lib/modal"
import { useVideoPlayer } from "lib/video-player"
import { overlaySubscriptionDataLayer } from "lib/gtm/interactions"
import { useAccesses } from "lib/accesses"

import { Products } from "components/common"
import { Slider } from "components/common"
import Image from "components/common/image"
import { Actions } from "components/subscription/film"
import SkeletonLink from "components/common/skeleton-link"
import { LoginSvodCard } from "components/subscription/login-card"

export const SliderRailProduct = ({ productIds, isSvod, withPreview, isOverlay }) => {

	return (
		<Products products={productIds} loading={false} getVideoIds={true}>
			{
				(products, loading) => (
					<LocalSlider
						products={products}
						loading={loading}
						isSvod={isSvod}
						isOverlay={isOverlay}
						withPreview={withPreview}
					/>
				)
			}
		</Products>
	)
}

const LocalSlider = ({ products, loading, isSvod, withPreview, isOverlay }) => (
	<Slider
		className={classNames("grid films", { "svod": isSvod })}
		navClassName="min"
		slides={products}
		slideClassName="swiper-slide"
		slideComponent={Product}
		slidePropKey="product"
		slideRelatedProps={{ withPreview, isOverlay }}
		loading={loading}
		asList={false}
		hidePagination={true}
	/>
)

const Product = ({ product, withPreview, isOverlay }) => {
	const { c } = useTranslation()
	const { user, showLoginSvod } = useAuth()
	const { hasSubscribed } = useAccesses()
	const { show } = useModal()
	const { seeFilm } = useVideoPlayer()

	const [hovered, setHovered] = useState(false)

	const handleMouseEnter = () => {
		if (withPreview) {
			setHovered(true)
		}
	}

	const handleMouseLeave = () => {
		setHovered(false)
	}

	const showCallToActionSvod = (product) => {
		show({
			id: "login-svod",
			contentClassName: "svod-color centered",
			children: <LoginSvodCard product={product} />,
			className: "modal short"
		})
	}

	const handleWatchMovie = (event, product) => {
		event.preventDefault()
		if (hasSubscribed) {
			seeFilm(product)

			return
		}
		if (user && !hasSubscribed) {
			showCallToActionSvod(product)

			return
		}
		showLoginSvod(product)
		overlaySubscriptionDataLayer(product)
	}

	return (
		<div
			className={classNames(
				"slide-content",
				{
					"preview-toggle": withPreview,
					"is-previewed": hovered
				}
			)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{!withPreview && (
				<SkeletonLink href="/film/[id]" as={`/film/${product.linkRewrite}`}>
					<a title={c("see-single-film")} className="more-link">
						<span className="label">{c("see-single-film")}</span>
					</a>
				</SkeletonLink>
			)}
			<div className="film-link">
				<figure className="cover" style={{ aspectRatio: "588/353" }}>
					<picture>
						<Image
							src={image(product, PRODUCT_PHOTOGRAMME)}
							alt={product.name}
						/>
					</picture>
				</figure>
				<div className="infos">
					<h3 className="title">{product.name}</h3>
					<span className="director">{product.director}</span>
					<span className="year">{product.year}</span>
					<span className="country">{product.origin}</span>
					<span className="duration">{product.duration}</span>
					{
						isOverlay
						&& <ul className="has-bonus">
							{
								product.bonusesExclusifsCount > 0
								&& <li className="exclusive">bonus</li>
							}
						</ul>
					}
					{
						product.endDate
						&& <p className="fixed svod-status">
							<span className="deadline">
								{`${c("until")} ${product.endDate}`}
							</span>
						</p>
					}
				</div>
				{withPreview && (
					<div className={classNames("preview film", { "active": hovered })}>
						<div className="preview-content">
							<figure className={"cover"} style={{ aspectRatio: "588/353" }}>
								<a
									href="#"
									rel="nofollow noopener"
									title={c("watch-the-movie")}
									data-overlay="#overlay-login-svod"
									className="icon-play video-link more-link"
									onClick={(event) => handleWatchMovie(event, product)}
								>
									<span className="label">{c("watch-the-movie")}</span>
								</a>
								<picture>
									<Image
										src={image(product, PRODUCT_PHOTOGRAMME)}
										alt={product.name}
									/>
								</picture>
							</figure>
							<div className="infos">
								<h3 className="title">{product.name}</h3>
								<span className="director">{product.director}</span>
								<span className="year">{product.year}</span>
								<span className="country">{product.origin}</span>
								<span className="duration">{product.duration}</span>
								{
									product.endDate
									&& <p className="fixed svod-status">
										<span className="deadline">
											{`${c("until")} ${product.endDate}`}
										</span>
									</p>
								}
								<div className="film-extras">
									<div className="synopsis" dangerouslySetInnerHTML={{ __html: product.description }} />
									<Actions product={product} />
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
